import { Component, EventEmitter, Input, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import {
  AlertModalComponent,
  modalTypes
} from '@components/modals/alert-modal/alert-modal.component';
import { constants } from '@constants/constants';
import {
  checkIsRecommendationsFollowed,
  getCurrentProductType,
  getCurrentProductTypeId,
  getDefaultRecommendationTabPerProduct,
  getIsOverwritedToGH,
  getIsOverwritedToGL,
  getIsOverwritedToGSM,
  getProfileColor,
  getRecommendationTabNameById,
  isCems,
  isCims,
  isPeri,
  isQuestion12AnsweredYes,
  openFileInNewTab
} from '@extensions/extensions';
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { MinAmountPerMode } from '@models/min-amount-per-mode';
import { PocheEnum } from '@models/poche';
import { ProfileStepName } from '@models/profile-step-name';
import { FundsService } from '@my-contract/funds.service';
import { LabelTextPipe } from '@pipes/label-text.pipe';
import { LabelsDataService } from '@services/labels-data.service';
import {
  OverridesOfRecommendationProfileSelection,
  temporary
} from '@shared/constants/temporary';
import { pushGtmTag } from '@shared/extensions/gtm.extentions';
import {
  getFundChartPaymentMinimumText,
  getMandatGestionUiLibelle
} from '@shared/extensions/recommendation.extensions';
import { PerformanceType } from '@shared/models/performance';
import { ProfileStepCode } from '@shared/models/profile-step-code';
import {
  FondsRepartition,
  MandatGestion,
  MandatGestion2,
  ModeGestionRecommandation,
  Poche,
  Recommandation
} from '@webapi/MIF.Subscription.Parrot';
import {
  DefaultProductTabs,
  ProductType,
  Profile
} from '@webapi/MIF.Subscription.WebApi';
import { CustomerService } from '@webapi/services/customer.service';
import { PerformanceProductType } from '@webapi/services/portfolio.service';
import { SettingsService } from '@webapi/services/settings.service';
import { ContratTransferableFourgousExtended } from '@webapi/services/ws-polices.service';
import { WsReferentielMetierService } from '@webapi/services/ws-referentiel-metier.service';
import { ChartData, ChartOptions } from 'chart.js';
import { BsModalService } from 'ngx-bootstrap/modal';

@Component({
  standalone: false,
  selector: 'app-profile-summary',
  templateUrl: './profile-summary.component.html',
  styleUrls: ['./profile-summary.component.scss']
})
export class ProfileSummaryComponent implements OnInit {
  @Input() recommandation: Recommandation;
  @Input() mandatGestion: MandatGestion2;
  @Input() questions: any;
  @Input() clientProfile: Profile;
  @Input() transferableContracts: ContratTransferableFourgousExtended[];
  public onTabChanged: EventEmitter<string> = new EventEmitter();

  isPeri: boolean = isPeri();
  isCims: boolean = isCims();
  isCems: boolean = isCems();
  productType: PerformanceProductType = isPeri()
    ? PerformanceProductType.PERI
    : PerformanceProductType.CEMS_CIMS;
  currentProductType = getCurrentProductType();
  currentProductTypeId = getCurrentProductTypeId();
  getMandatGestionUiLibelle = getMandatGestionUiLibelle;
  getFundChartPaymentMinimumText = getFundChartPaymentMinimumText;
  ProfileStepName = ProfileStepName;
  PerformanceType = PerformanceType;
  constants: any = constants;
  blueColor = '#009ad5';
  fondEuroText = 'Fonds en euros MIF';
  fondOthersText = 'Unités de compte';

  prudenteTooltipText = `<img src='assets/img/peri-prudente.png' alt=''/>`;
  equilibreeTooltipText = `<img src='assets/img/peri-equilibree.png' alt=''/>`;
  dynamiqueTooltipText = `<img src='assets/img/peri-dynamic.png' alt=''/>`;
  offensifTooltipText = `<img src='assets/img/peri-offensif.png' alt=''/>`;

  activeGestionLibreTab: boolean = true;
  activeGestionSousMandatTab: boolean;
  activeGestionHorizonTab: boolean;

  faInfoCircle = faInfoCircle;

  public chartData?: ChartData<'doughnut', number[], string>;
  public chartsOptions: ChartOptions<'doughnut'> = {
    cutout: '85%',
    plugins: {
      legend: { display: false }
    }
  };

  gestionLibre: ModeGestionRecommandation;
  gestionSousMandat: ModeGestionRecommandation;
  gestionHorizon: ModeGestionRecommandation;

  selectedMandatGestion: MandatGestion;

  fondsExpanded = false;
  expandedFonds: ExpandedFond[];
  expandedSereniteFonds: ExpandedFond[];
  currentPlanInitialPercentages: any;
  expandedAlternativeFonds: ExpandedFond[];

  calculatedPercentScore: number;
  chartPercentagesExpanded: any[];
  chartColorsExpanded: any[];
  chartLabelsExpanded: any[];
  amount: number;
  minAmountPerMode: MinAmountPerMode;
  securityOtherFond: FondsRepartition;
  profileTypeCode: number;
  managementTypeCode: number;
  selectedPlanName: string;
  profileTypeRecommended: any;
  selectedInvesmentProfile: number;
  offersCount: number = 0;
  clientProfileForViewOffer: Profile;
  sereniteFondsExpanded: boolean;
  alternativeFondsExpanded: boolean;
  sumSelectedMondatFondsPercentages: number;
  gestionHorizonMandatGestion: ModeGestionRecommandation;
  defaultProductTabsConfiguration: DefaultProductTabs;
  isTemporaryOverrided: boolean;
  chartLabels: any[];
  chartColors: any;
  chartPercentages: any;

  constructor(
    private labelsDataService: LabelsDataService,
    private bsModalService: BsModalService,
    private customerService: CustomerService,
    private wsReferentielMetierService: WsReferentielMetierService,
    private router: Router,
    private fundsService: FundsService,
    public labelTextPipe: LabelTextPipe,
    private settingsService: SettingsService
  ) {}

  async ngOnInit(): Promise<void> {
    await this.fetchConnfig();
    this.getAvailableModes();
    this.initCurrentRecommendation();
    this.selectedMandatGestion = Object.assign(this.mandatGestion ?? {});
    this.fetchAllPlansData(); // do not waiting user click and reusing data from local service storage after
    await this.fetchInitData();
    this.clientProfileForViewOffer = Object.assign(
      new Profile(),
      this.clientProfile
    );
    this.recalculateClientProfileDataForViewOffer();
  }

  async fetchInitData(): Promise<void> {
    this.minAmountPerMode =
      await this.wsReferentielMetierService.getMinAmountPerMode();
  }

  fetchAllPlansData(): void {
    // to speed up ui response when user changes the plan
    this.wsReferentielMetierService.getMandatGestion(constants.prudent.id); // 1
    this.wsReferentielMetierService.getMandatGestion(constants.modere.id); // 2
    this.wsReferentielMetierService.getMandatGestion(constants.offensif.id); // 4
    this.wsReferentielMetierService.getMandatGestion(constants.equilibré.id); // 5
    this.wsReferentielMetierService.getMandatGestion(constants.dynamiqueA.id); // 6

    if (this.isPeri) {
      this.wsReferentielMetierService.getMandatGestion(constants.dynamiqueR.id); // 3
    }
  }

  updateCharts(
    labels: string[],
    data: number[],
    backgroundColors: string[]
  ): void {
    this.chartData = {
      labels,
      datasets: [
        {
          data,
          backgroundColor: backgroundColors
        }
      ]
    };
  }

  async fetchConnfig(): Promise<void> {
    this.defaultProductTabsConfiguration =
      await this.settingsService.getDefaultProductTabsConfiguration();
  }

  getAvailableModes(): void {
    this.recommandation.produitRecommande.modeGestion.forEach(
      (mode: ModeGestionRecommandation) => {
        if (mode.libelle == constants.gestionHorizon.name) {
          this.gestionHorizon = mode;
          return;
        }
        if (mode.libelle == constants.gestionLibre.name) {
          this.gestionLibre = mode;
          return;
        }
        if (mode.libelle == constants.gestionSousMandat.name) {
          this.gestionSousMandat = mode;
          return;
        }
      }
    );

    this.processTabSelection();
  }

  processTabSelection() {
    const isTemporaryOverrided =
      this.processTemporaryOverridingTabAndProfileSelection(); // todo: delete when temporary override will be removed MIFS-704 CR in the comments
    if (isTemporaryOverrided) {
      return;
    }

    const isOverrided = this.processOverridingTabSelection();
    if (isOverrided) {
      return;
    }

    switch (this.currentProductTypeId) {
      case ProductType.Cems: {
        const defaultTabCems: number = getDefaultRecommendationTabPerProduct(
          this.defaultProductTabsConfiguration,
          constants.productNames.cems.id
        );
        this.changeTab(getRecommendationTabNameById(defaultTabCems));
        break;
      }
      case ProductType.Cims: {
        const defaultTabCims: number = getDefaultRecommendationTabPerProduct(
          this.defaultProductTabsConfiguration,
          constants.productNames.cims.id
        );
        this.changeTab(getRecommendationTabNameById(defaultTabCims));
        break;
      }
      case ProductType.Peri: {
        const defaultTabPeri: number = getDefaultRecommendationTabPerProduct(
          this.defaultProductTabsConfiguration,
          constants.productNames.peri.id
        );
        this.changeTab(getRecommendationTabNameById(defaultTabPeri));
        break;
      }
    }
  }

  processTemporaryOverridingTabAndProfileSelection(): boolean {
    // todo: delete when temporary override will be removed MIFS-704 CR in the comments
    this.isTemporaryOverrided = false;
    const overridesOfRecommendationProfileSelection =
      temporary.overridesOfRecommendationProfileSelection;

    if (
      !overridesOfRecommendationProfileSelection ||
      overridesOfRecommendationProfileSelection?.length === 0
    ) {
      return false;
    }

    switch (this.currentProductTypeId) {
      case ProductType.Cems: {
        const cemsAppOverrides =
          overridesOfRecommendationProfileSelection.filter(
            (override: OverridesOfRecommendationProfileSelection) => {
              return override.productId === constants.productNames.cems.id;
            }
          );
        const cemsAppOverrideForCurrentProfileType = cemsAppOverrides.filter(
          (override: OverridesOfRecommendationProfileSelection) => {
            return (
              override.matchProfileType === this.recommandation?.profil?.libelle
            );
          }
        )?.[0];

        if (cemsAppOverrideForCurrentProfileType) {
          this.isTemporaryOverrided = true;
          this.changeTab(cemsAppOverrideForCurrentProfileType.replaceToTabName);
          this.selectProfile(
            cemsAppOverrideForCurrentProfileType.replaceToProfileName,
            cemsAppOverrideForCurrentProfileType.replaceToTabName
          );
        }
        break;
      }
      case ProductType.Cims: {
        const cimsAppOverrides =
          overridesOfRecommendationProfileSelection.filter(
            (override: OverridesOfRecommendationProfileSelection) => {
              return override.productId === constants.productNames.cims.id;
            }
          );
        const cimsAppOverrideForCurrentProfileType = cimsAppOverrides.filter(
          (override: OverridesOfRecommendationProfileSelection) => {
            return (
              override.matchProfileType === this.recommandation?.profil?.libelle
            );
          }
        )?.[0];

        if (cimsAppOverrideForCurrentProfileType) {
          this.isTemporaryOverrided = true;
          this.changeTab(cimsAppOverrideForCurrentProfileType.replaceToTabName);
          this.selectProfile(
            cimsAppOverrideForCurrentProfileType.replaceToProfileName,
            cimsAppOverrideForCurrentProfileType.replaceToTabName
          );
        }

        break;
      }
      // continue switch list in case new temporary override items will be added
    }

    return this.isTemporaryOverrided;
  }

  processOverridingTabSelection(): boolean {
    const isOverwritedToGL = getIsOverwritedToGL(
      this.recommandation?.profil?.id
    );
    const isOverwritedToGH = getIsOverwritedToGH(
      this.recommandation?.profil?.id
    );
    const isOverwritedToGSM = getIsOverwritedToGSM(
      this.recommandation?.profil?.id
    );
    let isOverrided = false;

    if (this.isPeri && this.gestionHorizon && isOverwritedToGH) {
      isOverrided = true;
      this.changeTab(constants.gestionHorizon.name);
    } else if (this.gestionLibre && isOverwritedToGL) {
      isOverrided = true;
      this.changeTab(constants.gestionLibre.name);
    } else if (this.gestionSousMandat && isOverwritedToGSM) {
      isOverrided = true;
      this.changeTab(constants.gestionSousMandat.name);
    }

    return isOverrided;
  }

  get isQuestion12AnsweredYes(): boolean {
    return isQuestion12AnsweredYes(this.questions, this.clientProfile);
  }

  get isOneMandat(): boolean {
    return this.recommandation.produitRecommande.modeGestion.length === 1;
  }

  get mandatGestionId(): number {
    return this.recommandation.id;
  }

  get GHSereniteTitle(): string {
    const gHModeGestion = this.gestionHorizonMandatGestion;

    const serenitePoche = gHModeGestion?.poche.filter((poche: Poche) => {
      return poche.libelle === constants.periPoches.serenite.name;
    })[0];

    if (!serenitePoche) return '';

    return `${serenitePoche?.repartition} ${this.labelTextPipe.transform(
      'Recommendation.PocheExtraText1'
    )}`;
  }

  get GHSereniteLabel(): string {
    const gHModeGestion = this.gestionHorizonMandatGestion;

    const serenitePoche = gHModeGestion?.poche.filter((poche: Poche) => {
      return poche.libelle === constants.periPoches.serenite.name;
    })[0];

    if (!serenitePoche) return '';

    return serenitePoche?.libelle;
  }

  get GHAlternativeTitle(): string {
    const gHModeGestion = this.gestionHorizonMandatGestion;

    const alternativePoche = gHModeGestion?.poche.filter((poche: Poche) => {
      return poche.libelle === constants.periPoches.alternative.name;
    })[0];

    if (!alternativePoche) return '';

    return `${alternativePoche?.repartition}% sur la poche alternative`; // todo: Recommendation.PocheExtraTextAlternative
  }

  get GHAlternativeLabel(): string {
    const gHModeGestion = this.gestionHorizonMandatGestion;

    const alternativePoche = gHModeGestion?.poche.filter((poche: Poche) => {
      return poche.libelle === constants.periPoches.alternative.name;
    })[0];

    if (!alternativePoche) return '';

    return alternativePoche?.libelle;
  }

  get GHPerformanceTitle(): string {
    const gHModeGestion = this.gestionHorizonMandatGestion;

    const performancePoche = gHModeGestion?.poche.filter((poche: Poche) => {
      return poche.libelle === constants.periPoches.performance.name;
    })[0];

    if (!performancePoche) return '';

    return `${performancePoche?.repartition} ${this.labelTextPipe.transform(
      'Recommendation.PocheExtraText2'
    )}`;
  }

  get GHPerformanceLabel(): string {
    const gHModeGestion = this.gestionHorizonMandatGestion;

    const performancePoche = gHModeGestion?.poche.filter((poche: Poche) => {
      return poche.libelle === constants.periPoches.performance.name;
    })[0];

    if (!performancePoche) return '';

    return performancePoche?.libelle;
  }

  get GSMEuroFondsRepartition(): FondsRepartition {
    return this.selectedMandatGestion.allocation?.find(
      (rep: FondsRepartition) => {
        return rep.fonds?.id === 1;
      }
    );
  }

  get GSMEuroTitle(): string {
    return this.GSMEuroFondsRepartition?.titre;
  }

  get GSMEuroLabel(): string {
    return this.GSMEuroFondsRepartition?.libelle;
  }

  get GSMUCDistribution(): number {
    return 100 - this.GSMEuroFondsRepartition?.repartition;
  }

  get GLEuroTitle(): string {
    const GLModeGestion =
      this.recommandation.produitRecommande.modeGestion.filter(
        (mode: ModeGestionRecommandation) => {
          return mode.libelle === constants.gestionLibre.name;
        }
      )[0];

    const pocheEuros = GLModeGestion?.poche.filter((item: Poche) => {
      return item.libelle === PocheEnum.EURO;
    })[0];

    const euroFondsRepartition = pocheEuros.fondsRepartition[0];

    return euroFondsRepartition.titre;
  }

  get GLEuroLabel(): string {
    const GLModeGestion =
      this.recommandation.produitRecommande.modeGestion.filter(
        (mode: ModeGestionRecommandation) => {
          return mode.libelle === constants.gestionLibre.name;
        }
      )[0];

    const pocheEuros = GLModeGestion?.poche.filter((item: Poche) => {
      return item.libelle === PocheEnum.EURO;
    })[0];

    const euroFondsRepartition = pocheEuros.fondsRepartition[0];

    return euroFondsRepartition.libelle;
  }

  get GLUCTitle(): string {
    const GLModeGestion =
      this.recommandation.produitRecommande.modeGestion.filter(
        (mode: ModeGestionRecommandation) => {
          return mode.libelle === constants.gestionLibre.name;
        }
      )[0];

    const pocheUC = GLModeGestion?.poche.filter((item: Poche) => {
      return item.libelle === PocheEnum.UC;
    })[0];

    const notEuroFondsRepartition = pocheUC?.fondsRepartition[0];

    return notEuroFondsRepartition?.titre;
  }

  get GLUCLabel(): string {
    const GLModeGestion =
      this.recommandation.produitRecommande.modeGestion.filter(
        (mode: ModeGestionRecommandation) => {
          return mode.libelle === constants.gestionLibre.name;
        }
      )[0];

    const pocheUC = GLModeGestion?.poche.filter((item: Poche) => {
      return item.libelle === PocheEnum.UC;
    })[0];

    const notEuroFondsRepartition = pocheUC?.fondsRepartition[0];

    return notEuroFondsRepartition?.libelle;
  }

  initCurrentRecommendation(tab?: string): void {
    this.profileTypeRecommended = Object.assign(
      {},
      { plan: this.recommandation.profil.libelle }
    );
    if (this.isTemporaryOverrided) {
      return;
    }

    this.selectProfile(this.recommandation.profil.libelle, tab);
  }

  selectProfile(profileName: string, tab?: string): void {
    this.selectedPlanName = profileName; // uiName from constants

    switch (this.selectedPlanName) {
      case constants.sécuritaire.name: {
        this.switchIfSécuritaire();
        break;
      }
      case constants.prudent.uiName: {
        this.switchIfPrudente();
        break;
      }
      case constants.modere.uiName: {
        this.switchIfModere();
        break;
      }
      case constants.equilibré.uiName: {
        if (this.isPeri) {
          this.switchIfModere();
        } else {
          this.switchIfEquilibree();
        }

        break;
      }
      case constants.dynamiqueA.uiName: {
        this.switchIfDynamique();
        break;
      }
      case constants.dynamiqueR.uiName: {
        this.switchIfDynamique();
        break;
      }
      case constants.offensif.uiName: {
        if (tab === constants.gestionHorizon.name) {
          this.switchIfOffensiveGH();
        } else {
          this.switchIfOffensive();
        }
        break;
      }
      default:
        break;
    }
  }

  get profileColor(): string {
    return getProfileColor(this.recommandation.profil.libelle);
  }

  get securityProfileColor(): string {
    return constants.sécuritaireOtherColor;
  }

  showConditionsModal(): void {
    this.bsModalService.show(AlertModalComponent, {
      initialState: {
        type: modalTypes.info,
        body: this.labelsDataService.getData(
          'details.contactsRedBoxLinkContent'
        )
      },
      class: 'modal-lg'
    });
  }

  changeTab(profileName: string): void {
    switch (profileName) {
      case constants.gestionHorizon.name: {
        if (this.gestionHorizon) {
          this.activeGestionHorizonTab = true;
          this.activeGestionSousMandatTab = false;
          this.activeGestionLibreTab = false;
          this.managementTypeCode = constants.gestionHorizon.id;
          this.onTabChanged.next(profileName);
        } else {
          this.selectGLTab(); // for case no such tab by recommendation
        }
        break;
      }
      case constants.gestionSousMandat.name: {
        if (this.gestionSousMandat) {
          this.activeGestionSousMandatTab = true;
          this.activeGestionLibreTab = false;
          this.activeGestionHorizonTab = false;
          this.managementTypeCode = constants.gestionSousMandat.id;
          this.onTabChanged.next(profileName);
        } else {
          this.selectGLTab();
        }
        break;
      }
      case constants.gestionLibre.name: {
        if (this.gestionLibre) {
          this.activeGestionLibreTab = true;
          this.activeGestionSousMandatTab = false;
          this.activeGestionHorizonTab = false;
          this.managementTypeCode = constants.gestionLibre.id;
          this.onTabChanged.next(profileName);
        } else {
          this.selectGLTab();
        }
        break;
      }
      default: {
        this.selectGLTab();
      }
    }

    this.initCurrentRecommendation(profileName);
    this.collapseFonds();
  }

  selectGLTab(): void {
    this.activeGestionLibreTab = true;
    this.activeGestionSousMandatTab = false;
    this.activeGestionHorizonTab = false;
    this.managementTypeCode = constants.gestionLibre.id;
    this.onTabChanged.next(constants.recommendationTabs.gestionLibre.name);
  }

  getDefaultChartColors(isSecurity: boolean = false): any {
    return [
      this.blueColor,
      isSecurity ? constants.sécuritaireOtherColor : this.profileColor
    ];
  }

  getSecurityChartColors(): any {
    return [this.blueColor];
  }

  initExpandedFonds(): void {
    this.expandedFonds = [];
    let chartLabelsExpanded: string[] = [];
    let chartPercentagesExpanded: number[] = [];
    let chartColorsExpanded: string[] = [];
    let calculatedPercentScore = 0;

    if (this.activeGestionHorizonTab) {
      this.gestionHorizonMandatGestion?.poche
        .find((poche: Poche) => {
          return poche.libelle === PocheEnum.PPerf;
        })
        ?.fondsRepartition.forEach((item: FondsRepartition) => {
          const label = item.titre;
          chartPercentagesExpanded.push(item.repartition);
          chartColorsExpanded.push(this.profileColor);
          chartLabelsExpanded.push(label);
          calculatedPercentScore += item.repartition;
          this.expandedFonds.push({
            id: item.fonds.id,
            title: label,
            isEuro: false,
            file: item.fonds.document[0].fichierAttache,
            description: item.libelle
          } as ExpandedFond);
        });
    } else {
      this.selectedMandatGestion.allocation.forEach(
        (item: FondsRepartition) => {
          if (item.fonds.typeFonds.id === 1) {
            const label = `${
              this.currentPlanInitialPercentages[0][0]
            }${this.labelTextPipe.transform(
              'Recommendation.PercentageInvestedOnFundText'
            )}`;
            chartPercentagesExpanded.push(
              this.currentPlanInitialPercentages[0][0]
            );
            chartColorsExpanded.push(this.blueColor);
            chartLabelsExpanded.push(label);
            calculatedPercentScore += this
              .currentPlanInitialPercentages[0][0] as number;
            this.expandedFonds.push({
              id: item.fonds.id,
              title: label,
              isEuro: true,
              description: item.libelle
            } as ExpandedFond);
          } else {
            const label = item.titre;
            chartPercentagesExpanded.push(item.repartition);
            chartColorsExpanded.push(this.profileColor);
            chartLabelsExpanded.push(label);
            calculatedPercentScore += item.repartition;
            this.expandedFonds.push({
              id: item.fonds.id,
              title: label,
              isEuro: false,
              file: item.fonds.document[0].fichierAttache,
              description: item.libelle
            } as ExpandedFond);
          }
        }
      );

      this.moveEuroFontToTheTopOfExpandedList();
    }

    this.updateCharts(
      chartLabelsExpanded,
      chartPercentagesExpanded,
      chartColorsExpanded
    );
  }

  moveEuroFontToTheTopOfExpandedList(): void {
    this.expandedFonds.sort(x => (x.isEuro ? -1 : 1));
  }

  expandSereniteFonds(): void {
    this.sereniteFondsExpanded = true;
    this.initSereniteExpandedFonds();
  }

  initSereniteExpandedFonds() {
    this.expandedSereniteFonds = [];
    let chartLabelsExpanded: string[] = [];
    let chartPercentagesExpanded: number[] = [];
    let chartColorsExpanded: string[] = [];
    let calculatedPercentScore = 0;

    this.gestionHorizonMandatGestion?.poche
      .find((poche: Poche) => {
        return poche.libelle === PocheEnum.PSer;
      })
      ?.fondsRepartition.forEach((item: FondsRepartition) => {
        const label = item.titre ?? '';
        chartPercentagesExpanded.push(item.repartition);
        chartLabelsExpanded.push(label);
        calculatedPercentScore += this
          .currentPlanInitialPercentages[0][0] as number;
        // this.chartColorsExpanded[0].backgroundColor.push(item.fonds.id === 1 ? this.blueColor : this.profileColor);
        chartColorsExpanded.push(this.blueColor);
        this.expandedSereniteFonds.push({
          id: item.fonds.id,
          title: item.titre,
          //isEuro: item.fonds.id === 1,
          isEuro: true,
          description: item.libelle ?? ''
        } as ExpandedFond);
      });

    this.updateCharts(
      chartLabelsExpanded,
      chartPercentagesExpanded,
      chartColorsExpanded
    );
  }

  collapseSereniteFonds(): void {
    let chartLabels = this.getChartLabels();
    let chartColors = this.getDefaultChartColors();
    this.sereniteFondsExpanded = false;

    this.updateCharts(
      chartLabels,
      this.currentPlanInitialPercentages,
      chartColors
    );
  }

  expandAlternativeFonds(): void {
    this.alternativeFondsExpanded = true;
    this.initAlternativeExpandedFonds();
  }

  initAlternativeExpandedFonds() {
    this.expandedAlternativeFonds = [];
    this.chartLabelsExpanded = [];
    this.chartPercentagesExpanded = [];
    this.chartColorsExpanded = [
      {
        backgroundColor: []
      }
    ];
    this.calculatedPercentScore = 0;

    this.gestionHorizonMandatGestion?.poche
      .find((poche: Poche) => {
        return poche.libelle === PocheEnum.PAlternative;
      })
      ?.fondsRepartition.forEach((item: FondsRepartition) => {
        const label = item.titre ?? '';
        this.chartPercentagesExpanded.push(item.repartition);
        this.chartLabelsExpanded.push(label);
        this.calculatedPercentScore += this
          .currentPlanInitialPercentages[0][0] as number;
        this.chartColorsExpanded[0].backgroundColor.push(
          constants.alternativeFondsColor
        );
        this.expandedAlternativeFonds.push({
          id: item.fonds.id,
          title: item.titre,
          isEuro: false,
          isAlternative: true,
          description: item.libelle ?? ''
        } as ExpandedFond);
      });

    this.updateCharts(
      this.chartLabelsExpanded,
      this.chartPercentagesExpanded,
      this.chartColorsExpanded
    );
  }

  collapseAlternativeFonds(): void {
    this.alternativeFondsExpanded = false;
    this.chartLabels = this.getChartLabels();
    this.chartColors = this.getDefaultChartColors();
    this.updateCharts(
      this.chartLabels,
      this.currentPlanInitialPercentages,
      this.chartColors
    );
  }

  expandFonds(): void {
    this.fondsExpanded = true;
    this.initExpandedFonds();
  }

  collapseFonds(): void {
    this.fondsExpanded = false;
    let chartLabels = this.getChartLabels();
    let chartColors = this.getDefaultChartColors();

    this.updateCharts(
      chartLabels,
      this.currentPlanInitialPercentages,
      chartColors
    );
  }

  async getSelectedMandatManual(): Promise<void> {
    const doc = await this.wsReferentielMetierService.getDocumentsFichier(
      parseInt(this.selectedMandatGestion.document.id, 10)
    );

    if (doc) {
      await openFileInNewTab(doc.fichierAttache);
    }
  }

  getExpandedFondsColor(item: ExpandedFond): string {
    if (item.isAlternative) {
      return constants.alternativeFondsColor;
    }

    return item.isEuro ? this.blueColor : this.profileColor;
  }

  async getMandatManualByFondItem(item: ExpandedFond): Promise<void> {
    if (item.isEuro) {
      await this.getMandatManualById(1);
    } else if (item.id) {
      const fileBlob = await this.fundsService.getFundRegulatoryDocument(
        item.id,
        item.title
      );
      const fileURL = URL.createObjectURL(fileBlob);
      window.open(fileURL, '_blank');
    }
  }

  async getMandatManualById(id: number): Promise<void> {
    let fond;

    const fonds = this.selectedMandatGestion.allocation.filter(
      (item: FondsRepartition) => {
        return item.fonds.id === id;
      }
    );

    if (fonds && fonds.length > 0) {
      fond = fonds[0].fonds;

      const fileBlob = await this.fundsService.getFundRegulatoryDocument(
        fond.id,
        fond.libelle
      );
      const fileURL = URL.createObjectURL(fileBlob);
      window.open(fileURL, '_blank');
    }
  }

  async getSecuritaireMandatManualEuroById(id: number): Promise<void> {
    let fond;

    const GL = this.recommandation.produitRecommande.modeGestion.find(
      (mode: ModeGestionRecommandation) => {
        return mode.id === constants.gestionLibre.id;
      }
    );

    const euroPoche = GL.poche.find((poche: Poche) => {
      return poche.libelle === PocheEnum.EURO;
    });

    const fonds = euroPoche.fondsRepartition.filter(
      (item: FondsRepartition) => {
        return item.fonds.id === id;
      }
    );

    if (fonds && fonds.length > 0) {
      fond = fonds[0].fonds;

      const fileBlob = await this.fundsService.getFundRegulatoryDocument(
        fond.id,
        fond.libelle
      );
      const fileURL = URL.createObjectURL(fileBlob);
      window.open(fileURL, '_blank');
    }
  }

  async getSecuritaireMandatManualUc(): Promise<void> {
    // Securitaire 75/25
    let fond;

    const GL = this.recommandation.produitRecommande.modeGestion.find(
      (mode: ModeGestionRecommandation) => {
        return mode.id === constants.gestionLibre.id;
      }
    );

    const euroPoche = GL.poche.find((poche: Poche) => {
      return poche.libelle === PocheEnum.UC;
    });

    const fonds = euroPoche.fondsRepartition.filter(
      (item: FondsRepartition) => {
        return item.fonds;
      }
    );

    if (fonds && fonds.length > 0) {
      fond = fonds[0].fonds;

      const fileBlob = await this.fundsService.getFundRegulatoryDocument(
        fond.id,
        fond.libelle
      );
      const fileURL = URL.createObjectURL(fileBlob);
      window.open(fileURL, '_blank');
    }
  }

  onOffersCountChanged(count: number) {
    this.offersCount = count;
  }

  async getGestionLibreCprDoc(): Promise<void> {
    const ucPoche = this.gestionLibre.poche.filter((item: Poche) => {
      return item.libelle === PocheEnum.UC;
    })[0];

    const fonds = ucPoche?.fondsRepartition.filter((item: FondsRepartition) => {
      return item.fonds.id !== 1;
    });

    if (fonds && fonds.length > 0) {
      const files = fonds[0].fonds.document?.filter((doc: any) => {
        return doc?.type?.id === constants.docTypeIdToRetriveFromRecommendation;
      });

      if (files?.length > 0) {
        await openFileInNewTab(files[0].fichierAttache);
      }
    }
  }

  recalculateClientProfileDataForViewOffer(): void {
    const isRecommendationsFollowed: boolean = checkIsRecommendationsFollowed(
      this.selectedPlanName,
      this.profileTypeRecommended.plan
    );
    const allocations = this.getAllocations(this.managementTypeCode);
    const investmentProfile = {
      recommendedProfileId: this.recommandation.profil.id,
      selectedProfileId: this.selectedInvesmentProfile,
      managementTypeCode: this.managementTypeCode
    } as InvestmentProfile;

    this.clientProfileForViewOffer =
      this.customerService.updateFondsModelWithoutSaving(
        this.clientProfile,
        allocations,
        investmentProfile,
        this.profileTypeCode,
        isRecommendationsFollowed
      );
  }

  // getChartPercentages(): MultiDataSet {
  getChartPercentages(): any {
    this.sumSelectedMondatFondsPercentages = 0;

    if (this.activeGestionHorizonTab) {
      if (!this.gestionHorizonMandatGestion) {
        return [];
      }

      const pocheSer = this.gestionHorizonMandatGestion.poche.find(
        (poche: Poche) => {
          return poche.libelle === PocheEnum.PSer;
        }
      );
      const pocheAlternative = this.gestionHorizonMandatGestion.poche.find(
        (poche: Poche) => {
          return poche.libelle === PocheEnum.PAlternative;
        }
      );
      const pochePerf = this.gestionHorizonMandatGestion.poche.find(
        (poche: Poche) => {
          return poche.libelle === PocheEnum.PPerf;
        }
      );

      // todo: check
      return pocheAlternative
        ? [
            [
              +pocheSer.repartition,
              +pocheAlternative.repartition,
              +pochePerf.repartition
            ]
          ]
        : [[+pocheSer.repartition, +pochePerf.repartition]];
    } else {
      this.selectedMandatGestion.allocation
        .filter((item: FondsRepartition) => {
          return item.fonds.id !== 1;
        })
        .forEach((item: FondsRepartition) => {
          this.sumSelectedMondatFondsPercentages += item.repartition;
        });

      return [
        100 - this.sumSelectedMondatFondsPercentages,
        this.sumSelectedMondatFondsPercentages
      ];
    }
  }

  // getChartLabels(): Label[] {
  getChartLabels(): any[] {
    if (this.activeGestionHorizonTab) {
      return [PocheEnum.PSer, PocheEnum.PAlternative, PocheEnum.PPerf];
    } else {
      return [this.fondEuroText, this.fondOthersText];
    }
  }

  // getSecurityChartPercentages(): MultiDataSet {
  getSecurityChartPercentages(): any {
    let notEuroPercentages = 0;

    if (
      this.recommandation.id === constants.securitaireProfileLess40KId ||
      this.recommandation.id === constants.securitaireProfileCims
    ) {
      return [100];
    }

    notEuroPercentages +=
      +this.recommandation.produitRecommande.modeGestion[0]?.poche.filter(
        (item: Poche) => {
          return item.libelle === PocheEnum.UC;
        }
      )[0]?.repartition;

    return [100 - notEuroPercentages, notEuroPercentages];
  }

  async switchIfSécuritaire(): Promise<void> {
    if (this.recommandation.id === constants.securitaireProfileMore40KId) {
      this.securityOtherFond =
        this.recommandation.produitRecommande.modeGestion[0]?.poche.filter(
          (item: Poche) => {
            return item.libelle === PocheEnum.UC;
          }
        )[0]?.fondsRepartition[0];
    }

    this.selectedInvesmentProfile = null;
    this.profileTypeCode = this.recommandation.id;
    this.selectedPlanName = constants.sécuritaire.name;
    let chartPercentages = this.getSecurityChartPercentages();
    this.currentPlanInitialPercentages = Object.assign({}, chartPercentages);
    let chartLabels =
      this.recommandation.id === constants.securitaireProfileLess40KId
        ? [this.fondEuroText]
        : [this.fondEuroText, this.securityOtherFond?.fonds.libelle];
    let chartColors =
      this.recommandation.id === constants.securitaireProfileLess40KId
        ? this.getSecurityChartColors()
        : this.getDefaultChartColors(true);
    this.updateCharts(chartLabels, chartPercentages, chartColors);
    this.recalculateClientProfileDataForViewOffer();
    console.log(
      `this.selectedMandatGestion.libelle: ${this.selectedMandatGestion?.libelle}`
    );
  }

  async switchIfPrudente(): Promise<void> {
    console.log('switchIfPrudente');
    if (this.activeGestionHorizonTab) {
      if (this.sereniteFondsExpanded) {
        this.sereniteFondsExpanded = false;
      }
      const montantVerse = this.clientProfile.initialPaymentAmount || 0; // initialPaymentAmount - it's one field for free payment amount and first payment amount
      const dateEstimationDepartRetraite = this.isPeri
        ? this.clientProfile.retirementPlan.expectedRetirementDate
        : null;
      const flagDurabilite = this.clientProfile.isQuestion12AnsweredYes(
        this.questions
      );
      const recommandationResponse =
        await this.wsReferentielMetierService.getRecommandation(
          constants.getRecommandationScores.prudente,
          !!this.clientProfile.transferContractCode,
          montantVerse,
          false,
          dateEstimationDepartRetraite,
          flagDurabilite
        );
      this.gestionHorizonMandatGestion =
        recommandationResponse.recommandation.produitRecommande.modeGestion.find(
          (item: ModeGestionRecommandation) => {
            return item.libelle === constants.gestionHorizon.name;
          }
        );
      this.selectedMandatGestion = new MandatGestion2({
        ...this.gestionHorizonMandatGestion.mandatGestion
      });
      this.selectedMandatGestion.allocation = [];
      this.gestionHorizonMandatGestion.poche
        .map((item: Poche) => {
          return item.fondsRepartition;
        })
        .forEach((frs: FondsRepartition[]) => {
          frs?.forEach((fr: FondsRepartition) => {
            this.selectedMandatGestion.allocation.push(fr);
          });
        });
    } else {
      const allMondatGestions = await (
        await this.wsReferentielMetierService.getMandatGestion(
          constants.prudent.id
        )
      ).mandatGestion;
      allMondatGestions.forEach((item: MandatGestion2) => {
        if (item.id === constants.prudent.id) {
          this.selectedMandatGestion = item;
        }
      });
    }

    this.selectedInvesmentProfile = constants.prudent.id;
    this.profileTypeCode = constants.prudent.id;
    this.selectedPlanName = constants.prudent.name;
    let chartPercentages = this.getChartPercentages();
    this.currentPlanInitialPercentages = this.getChartPercentages();
    let chartLabels = this.getChartLabels();
    let chartColors = this.getDefaultChartColors();
    this.updateCharts(chartLabels, chartPercentages, chartColors);
    this.collapseFonds();
    this.recalculateClientProfileDataForViewOffer();
    console.log(
      `this.selectedMandatGestion.libelle: ${this.selectedMandatGestion?.libelle}`
    );
  }

  async switchIfModere(): Promise<void> {
    console.log('switchIfModere');
    if (this.activeGestionHorizonTab) {
      if (this.sereniteFondsExpanded) {
        this.sereniteFondsExpanded = false;
      }
      const montantVerse = this.clientProfile.initialPaymentAmount || 0; // initialPaymentAmount - it's one field for free payment amount and first payment amount
      const dateEstimationDepartRetraite = this.isPeri
        ? this.clientProfile.retirementPlan.expectedRetirementDate
        : null;
      const flagDurabilite = this.clientProfile.isQuestion12AnsweredYes(
        this.questions
      );
      const recommandationResponse =
        await this.wsReferentielMetierService.getRecommandation(
          constants.getRecommandationScores.modere,
          !!this.clientProfile.transferContractCode,
          montantVerse,
          false,
          dateEstimationDepartRetraite,
          flagDurabilite
        );
      this.gestionHorizonMandatGestion =
        recommandationResponse.recommandation.produitRecommande.modeGestion.find(
          (item: ModeGestionRecommandation) => {
            return item.libelle === constants.gestionHorizon.name;
          }
        );
      this.selectedMandatGestion = new MandatGestion2({
        ...this.gestionHorizonMandatGestion.mandatGestion
      });
      this.selectedMandatGestion.allocation = [];
      this.gestionHorizonMandatGestion.poche
        .map((item: Poche) => {
          return item.fondsRepartition;
        })
        .forEach((frs: FondsRepartition[]) => {
          frs?.forEach((fr: FondsRepartition) => {
            this.selectedMandatGestion.allocation.push(fr);
          });
        });
    } else {
      const allMondatGestions = await (
        await this.wsReferentielMetierService.getMandatGestion(
          constants.modere.id
        )
      ).mandatGestion;
      allMondatGestions.forEach((item: MandatGestion2) => {
        if (item.id === constants.modere.id) {
          this.selectedMandatGestion = item;
        }
      });
    }

    this.selectedInvesmentProfile = constants.modere.id;
    this.profileTypeCode = constants.modere.id;
    this.selectedPlanName = constants.modere.name;
    this.chartPercentages = this.getChartPercentages();
    this.currentPlanInitialPercentages = this.getChartPercentages();
    this.chartLabels = this.getChartLabels();
    this.chartColors = this.getDefaultChartColors();
    this.updateCharts(
      this.chartLabels,
      this.chartPercentages,
      this.chartColors
    );
    this.collapseFonds();
    this.recalculateClientProfileDataForViewOffer();
    console.log(
      `this.selectedMandatGestion.libelle: ${this.selectedMandatGestion?.libelle}`
    );
  }

  async switchIfEquilibree(): Promise<void> {
    console.log('switchIfEquilibree');
    if (this.activeGestionHorizonTab) {
      if (this.sereniteFondsExpanded) {
        this.sereniteFondsExpanded = false;
      }
      const montantVerse = this.clientProfile.initialPaymentAmount || 0; // initialPaymentAmount - it's one field for free payment amount and first payment amount
      const dateEstimationDepartRetraite = this.isPeri
        ? this.clientProfile.retirementPlan.expectedRetirementDate
        : null;
      const flagDurabilite = this.clientProfile.isQuestion12AnsweredYes(
        this.questions
      );
      const recommandationResponse =
        await this.wsReferentielMetierService.getRecommandation(
          constants.getRecommandationScores.equilibree,
          !!this.clientProfile.transferContractCode,
          montantVerse,
          false,
          dateEstimationDepartRetraite,
          flagDurabilite
        );
      this.gestionHorizonMandatGestion =
        recommandationResponse.recommandation.produitRecommande.modeGestion.find(
          (item: ModeGestionRecommandation) => {
            return item.libelle === constants.gestionHorizon.name;
          }
        );
      this.selectedMandatGestion = new MandatGestion2({
        ...this.gestionHorizonMandatGestion.mandatGestion
      });
      this.selectedMandatGestion.allocation = [];
      this.gestionHorizonMandatGestion.poche
        .map((item: Poche) => {
          return item.fondsRepartition;
        })
        .forEach((frs: FondsRepartition[]) => {
          frs?.forEach((fr: FondsRepartition) => {
            this.selectedMandatGestion.allocation.push(fr);
          });
        });
    } else {
      const allMondatGestions = await (
        await this.wsReferentielMetierService.getMandatGestion(
          constants.equilibré.id
        )
      ).mandatGestion;
      allMondatGestions.forEach((item: MandatGestion2) => {
        if (item.id === constants.equilibré.id) {
          this.selectedMandatGestion = item;
        }
      });
    }

    this.selectedInvesmentProfile = constants.equilibré.id;
    this.profileTypeCode = constants.equilibré.id;
    this.selectedPlanName = constants.equilibré.name;
    let chartPercentages = this.getChartPercentages();
    this.currentPlanInitialPercentages = this.getChartPercentages();
    let chartLabels = this.getChartLabels();
    let chartColors = this.getDefaultChartColors();
    this.updateCharts(chartLabels, chartPercentages, chartColors);
    this.collapseFonds();
    this.recalculateClientProfileDataForViewOffer();
    console.log(
      `this.selectedMandatGestion.libelle: ${this.selectedMandatGestion?.libelle}`
    );
  }

  async switchIfDynamique(): Promise<void> {
    console.log('switchIfDynamique');
    const currentDynamiqueId = this.isPeri
      ? constants.dynamiqueR.id
      : constants.dynamiqueA.id;

    if (this.activeGestionHorizonTab) {
      if (this.sereniteFondsExpanded) {
        this.sereniteFondsExpanded = false;
      }
      const montantVerse = this.clientProfile.initialPaymentAmount || 0; // initialPaymentAmount - it's one field for free payment amount and first payment amount
      const dateEstimationDepartRetraite = this.isPeri
        ? this.clientProfile.retirementPlan.expectedRetirementDate
        : null;
      const flagDurabilite = this.clientProfile.isQuestion12AnsweredYes(
        this.questions
      );
      const recommandationResponse =
        await this.wsReferentielMetierService.getRecommandation(
          constants.getRecommandationScores.dynamique,
          !!this.clientProfile.transferContractCode,
          montantVerse,
          false,
          dateEstimationDepartRetraite,
          flagDurabilite
        );
      this.gestionHorizonMandatGestion =
        recommandationResponse.recommandation.produitRecommande.modeGestion.find(
          (item: ModeGestionRecommandation) => {
            return item.libelle === constants.gestionHorizon.name;
          }
        );
      this.selectedMandatGestion = new MandatGestion2({
        ...this.gestionHorizonMandatGestion.mandatGestion
      });
      this.selectedMandatGestion.allocation = [];
      this.gestionHorizonMandatGestion.poche
        .map((item: Poche) => {
          return item.fondsRepartition;
        })
        .forEach((frs: FondsRepartition[]) => {
          frs?.forEach((fr: FondsRepartition) => {
            this.selectedMandatGestion.allocation.push(fr);
          });
        });
    } else {
      const allMondatGestions = await (
        await this.wsReferentielMetierService.getMandatGestion(
          currentDynamiqueId
        )
      ).mandatGestion;
      allMondatGestions.forEach((item: MandatGestion2) => {
        if (item.id === currentDynamiqueId) {
          this.selectedMandatGestion = item;
        }
      });
    }

    this.selectedInvesmentProfile = currentDynamiqueId;
    this.profileTypeCode = currentDynamiqueId;
    this.selectedPlanName = this.isPeri
      ? constants.dynamiqueR.name
      : constants.dynamiqueA.name;
    this.chartPercentages = this.getChartPercentages();
    this.currentPlanInitialPercentages = this.getChartPercentages();
    let chartLabels = this.getChartLabels();
    let chartColors = this.getDefaultChartColors();
    this.updateCharts(chartLabels, this.chartPercentages, chartColors);
    this.collapseFonds();
    this.recalculateClientProfileDataForViewOffer();
    console.log(
      `this.selectedMandatGestion.libelle: ${this.selectedMandatGestion?.libelle}`
    );
  }

  async switchIfOffensive(): Promise<void> {
    console.log('switchIfOffensive');
    const allMondatGestions = await (
      await this.wsReferentielMetierService.getMandatGestion(
        constants.offensif.id
      )
    ).mandatGestion;
    allMondatGestions.forEach((item: MandatGestion2) => {
      if (item.id === constants.offensif.id) {
        this.selectedMandatGestion = item;
      }
    });
    this.selectedInvesmentProfile = constants.offensif.id;
    this.profileTypeCode = constants.offensif.id;
    this.selectedPlanName = constants.offensif.name;
    let chartPercentages = this.getChartPercentages();
    this.currentPlanInitialPercentages = this.getChartPercentages();
    let chartLabels = this.getChartLabels();
    let chartColors = this.getDefaultChartColors();
    this.updateCharts(chartLabels, chartPercentages, chartColors);
    this.collapseFonds();
    this.recalculateClientProfileDataForViewOffer();
    console.log(
      `this.selectedMandatGestion.libelle: ${this.selectedMandatGestion?.libelle}`
    );
  }

  async switchIfOffensiveGH(): Promise<void> {
    console.log('switchIfOffensiveGH');
    if (this.sereniteFondsExpanded) {
      this.sereniteFondsExpanded = false;
    }
    const montantVerse = this.clientProfile.initialPaymentAmount || 0; // initialPaymentAmount - it's one field for free payment amount and first payment amount
    const dateEstimationDepartRetraite = this.isPeri
      ? this.clientProfile.retirementPlan.expectedRetirementDate
      : null;
    const flagDurabilite = this.clientProfile.isQuestion12AnsweredYes(
      this.questions
    );
    const recommandationResponse =
      await this.wsReferentielMetierService.getRecommandation(
        constants.getRecommandationScores.offensiveGH,
        !!this.clientProfile.transferContractCode,
        montantVerse,
        false,
        dateEstimationDepartRetraite,
        flagDurabilite
      );
    this.gestionHorizonMandatGestion =
      recommandationResponse.recommandation.produitRecommande.modeGestion.find(
        (item: ModeGestionRecommandation) => {
          return item.libelle === constants.gestionHorizon.name;
        }
      );
    this.selectedMandatGestion = new MandatGestion2({
      ...this.gestionHorizonMandatGestion.mandatGestion
    });
    this.selectedMandatGestion.allocation = [];
    this.gestionHorizonMandatGestion.poche
      .map((item: Poche) => {
        return item.fondsRepartition;
      })
      .forEach((frs: FondsRepartition[]) => {
        frs?.forEach((fr: FondsRepartition) => {
          this.selectedMandatGestion.allocation.push(fr);
        });
      });

    this.selectedInvesmentProfile = constants.offensif.id;
    this.profileTypeCode = constants.offensif.id;
    this.selectedPlanName = constants.offensif.name;
    this.chartPercentages = this.getChartPercentages();
    this.currentPlanInitialPercentages = this.getChartPercentages();
    this.chartLabels = this.getChartLabels();
    this.chartColors = this.getDefaultChartColors();
    this.updateCharts(
      this.chartLabels,
      this.chartPercentages,
      this.chartColors
    );
    this.collapseFonds();
    this.recalculateClientProfileDataForViewOffer();
    console.log(
      `this.selectedMandatGestion.libelle: ${this.selectedMandatGestion?.libelle}`
    );
  }

  get isLessThanMinAmount(): boolean {
    this.amount = 0;

    if (this.activeGestionLibreTab) {
      return false;
    }

    if (this.clientProfile.initialPaymentAmount) {
      this.amount += this.clientProfile.initialPaymentAmount;
    }

    if (!this.isPeri) {
      if (
        this.clientProfile.transferContractCode &&
        this.transferableContracts?.length > 0
      ) {
        this.transferableContracts.forEach(
          (item: ContratTransferableFourgousExtended) => {
            if (
              item.numeroContrat === this.clientProfile.transferContractCode
            ) {
              this.amount += item.valeurRachat;
            }
          }
        );
      }

      return this.amount < this.minAmountPerMode?.GSM;
    } else {
      // peri
      if (this.activeGestionLibreTab) {
        return this.amount < this.minAmountPerMode?.GL;
      }
      if (this.activeGestionSousMandatTab) {
        return this.amount < this.minAmountPerMode?.GSM;
      }
      if (this.activeGestionHorizonTab) {
        return this.amount < this.minAmountPerMode?.GH;
      } else return false;
    }
  }

  get minAmountPerModeValue(): number {
    if (this.activeGestionLibreTab) {
      return this.minAmountPerMode?.GL;
    }
    if (this.activeGestionSousMandatTab) {
      return this.minAmountPerMode?.GSM;
    }
    if (this.activeGestionHorizonTab) {
      return this.minAmountPerMode?.GH;
    }
    return 0;
  }

  get isPrudente(): boolean {
    return this.selectedMandatGestion?.libelle === constants.prudent.name;
  }

  get isModere(): boolean {
    return this.selectedMandatGestion?.libelle === constants.modere.name;
  }

  get isEquilibree(): boolean {
    return this.selectedMandatGestion?.libelle === constants.equilibré.name;
  }

  get isDynamique(): boolean {
    return (
      this.selectedMandatGestion?.libelle === constants.dynamiqueA.name ||
      this.selectedMandatGestion?.libelle === constants.dynamiqueR.name
    );
  }

  get isOffensive(): boolean {
    return this.selectedMandatGestion?.libelle === constants.offensif.name;
  }

  get isSecurityProfileRecommended(): boolean {
    return this.recommandation.profil?.libelle === constants.sécuritaire.name;
  }

  get submitRecommendationGHText(): string {
    const isRecommendationsFollowed = checkIsRecommendationsFollowed(
      this.selectedPlanName,
      this.profileTypeRecommended.plan
    );

    return isRecommendationsFollowed
      ? `${this.labelTextPipe.transform(
          'Recommendation.submitRecommendationGHText1'
        )} ’${getMandatGestionUiLibelle(
          this.selectedMandatGestion?.libelle,
          PerformanceType.GH
        )}’` // I_FOLLOW_THE_RECOMMANDATION_AND_CONTINUE
      : `${this.labelTextPipe.transform(
          'Recommendation.submitRecommendationGHText2'
        )} ’${getMandatGestionUiLibelle(
          this.selectedMandatGestion?.libelle,
          PerformanceType.GH
        )}’`; // I_ACCEPT_NOT_TO_FOLLOW_RECOMMANDATION_AND_CONTINUE
  }

  get submitRecommendationGSMText(): string {
    const isRecommendationsFollowed = checkIsRecommendationsFollowed(
      this.selectedPlanName,
      this.profileTypeRecommended.plan
    );

    return isRecommendationsFollowed
      ? `${this.labelTextPipe.transform(
          'Recommendation.submitRecommendationGSMText1'
        )} ’${getMandatGestionUiLibelle(this.selectedMandatGestion?.libelle)}’` // I_FOLLOW_THE_RECOMMANDATION_AND_CONTINUE
      : `${this.labelTextPipe.transform(
          'Recommendation.submitRecommendationGSMText2'
        )} ’${getMandatGestionUiLibelle(this.selectedMandatGestion?.libelle)}’`; // I_ACCEPT_NOT_TO_FOLLOW_RECOMMANDATION_AND_CONTINUE
  }

  get fundChartPaymentMinimumText(): string {
    if (this.isCems) {
      return this.labelTextPipe.transform(
        'Recommendation.FundChartPaymentMinimumCEMS'
      );
    } else if (this.isPeri) {
      return this.labelTextPipe.transform(
        'Recommendation.FundChartPaymentMinimumPERI'
      );
    } else if (this.isCims) {
      return this.labelTextPipe.transform(
        'Recommendation.FundChartPaymentMinimumCIMS'
      );
    } else return '';
  }

  get recommandationProfilLibelle(): string {
    // should be this.recommandation?.profil?.libelle
    // todo: delete when temporary override will be removed MIFS-704 CR in the comments
    if (
      (this.isCems || this.isCims) &&
      this.recommandation?.profil?.libelle === constants.equilibré.name
    ) {
      return constants.modere.name;
    } else return this.recommandation?.profil?.libelle || '';
  }

  periSecurityProfileBtnClick(): void {
    window.open(constants.links.periSecurityProfileBtnLink, '_blank');
  }

  getAllocations(managementTypeCode: number): FondsRepartition[] {
    let allocations: FondsRepartition[] = [];

    if (
      managementTypeCode === constants.gestionSousMandat.id ||
      managementTypeCode === constants.gestionHorizon.id
    ) {
      allocations = this.selectedMandatGestion?.allocation;
    } else {
      this.gestionLibre.poche
        .map((item: Poche) => {
          return item.fondsRepartition;
        })
        .forEach((frs: FondsRepartition[]) => {
          frs?.forEach((fr: FondsRepartition) => {
            allocations.push(fr);
          });
        });
    }

    return allocations;
  }

  async submitRecommendation(): Promise<void> {
    const isRecommendationsFollowed: boolean = checkIsRecommendationsFollowed(
      this.selectedPlanName,
      this.profileTypeRecommended.plan
    );
    const allocations = this.getAllocations(this.managementTypeCode);
    const investmentProfile = {
      recommendedProfileId: this.recommandation.profil.id,
      selectedProfileId: this.selectedInvesmentProfile,
      managementTypeCode: this.managementTypeCode
    } as InvestmentProfile;

    await this.customerService.updateFondsModel(
      allocations,
      investmentProfile,
      this.profileTypeCode,
      isRecommendationsFollowed
    );

    pushGtmTag(ProfileStepCode.MyProfileStepSummary);

    this.router.navigate(['my-contract'], {
      queryParams: { userId: this.customerService.userId },
      state: { mismatchCheck: true }
    });
  }

  goToMyProfile(): void {
    this.router.navigate(['my-profile'], {
      queryParams: { userId: this.customerService.userId }
    });
  }

  async notAcceptRecommendation(): Promise<void> {
    const isRecommendationsFollowed = false;
    const investmentProfile = {
      recommendedProfileId: this.recommandation.profil.id,
      selectedProfileId: this.activeGestionSousMandatTab
        ? this.selectedInvesmentProfile
        : null,
      managementTypeCode: this.managementTypeCode
    } as InvestmentProfile;

    const allocations = this.getAllocations(this.managementTypeCode);
    await this.customerService.updateFondsModel(
      allocations,
      investmentProfile,
      this.profileTypeCode,
      isRecommendationsFollowed
    );

    pushGtmTag(ProfileStepCode.MyProfileStepSummary);

    this.router.navigate(['my-contract'], {
      queryParams: { userId: this.customerService.userId },
      state: { fromRecommendation: true }
    });
  }
}

export class ExpandedFond {
  id?: number;
  title: string;
  description?: string;
  file?: string;
  isEuro: boolean;
  isAlternative?: boolean;
}

export class InvestmentProfile {
  recommendedProfileId?: number;
  selectedProfileId?: number;
  managementTypeCode: number;
}
